// src/components/Navbar.js
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  Toolbar,
  IconButton,
  AppBar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
  AccountCircle,
  Settings,
  ExitToApp,
  Assessment,
  Image,
  QrCode,
  Public,
  Search,
  AdminPanelSettings,
  Dashboard,
  Inventory,
  History,
  PhotoLibrary,
  BrokenImage,
  Help,
  Business,
  HelpOutline as HelpOutlineIcon,
  AutoFixHigh as AutoFixHighIcon,
  Group
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Create the cohesive theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#f57c00',
      light: '#ffb74d',
      dark: '#ef6c00',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
      letterSpacing: '0.5px',
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          borderRight: '1px solid #e2e8f0',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            borderLeft: '3px solid #1976d2',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.16)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
        },
      },
    },
  },
});

const Navbar = ({ children }) => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    audit: false,
    images: false,
    barcodes: false,
    admin: false,
    master: false
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
    handleMenuClose();
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const menuItems = [
    {
      key: 'admin',
      label: 'Admin',
      icon: <AdminPanelSettings />,
      expandable: true,
      show: user?.role === 'admin',
      children: [
        { 
          label: 'User Management', 
          link: '/admin/users', 
          icon: <AccountCircle />,
          chip: 'Admin'
        },
        { 
          label: 'Login History', 
          link: '/admin/login-history', 
          icon: <History />,
          chip: 'Admin'
        },
        {
          label: 'Website',
          icon: <Public />,
          chip: 'Admin',
          action: () => {
            // Create a hidden form
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://web.44443.8664.wiki/admin/authenticate.php';
            form.target = '_blank';
            
            // Add email field
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'email';
            emailInput.value = 'ikigai.ngo@pm.me';
            form.appendChild(emailInput);
            
            // Add password field
            const passInput = document.createElement('input');
            passInput.type = 'hidden';
            passInput.name = 'password';
            passInput.value = '1234567898';
            form.appendChild(passInput);
            
            // Add to DOM and submit
            document.body.appendChild(form);
            form.submit();
            
            // Clean up after submission
            setTimeout(() => document.body.removeChild(form), 1000);
          }
        }
      ]
    },
    {
      key: 'audit',
      label: 'Audit',
      icon: <Assessment />,
      expandable: true,
      children: [
        { label: 'Landing', link: '/audit/landing', icon: <Dashboard /> },
        { label: 'Audit Items', link: '/audit/items', icon: <Help /> },
        { label: 'Logs', link: '/audit/logs', icon: <History /> },
      ]
    },
    {
      key: 'images',
      label: 'Images',
      icon: <Image />,
      expandable: true,
      children: [
        { label: 'Landing', link: '/images/landing', icon: <Dashboard /> },
        { label: 'Processed', link: '/images/processed', icon: <PhotoLibrary /> },
        { label: 'Logs', link: '/images/logs', icon: <History /> },
        { label: 'AI Training', link: '/images/ai-training', icon: <AutoFixHighIcon /> },
        // { label: '99 Undefined', link: '/images/undefined', icon: <HelpOutlineIcon /> }
      ]
    },
    {
      key: 'barcodes',
      label: 'Barcodes & Brands',
      icon: <QrCode />,
      expandable: true,
      children: [
        { label: 'Landing', link: '/barcodes/landing', icon: <Dashboard /> },
        { label: 'Unknown', link: '/barcodes/unknown', icon: <Help /> },
        { label: 'Library', link: '/barcodes/library', icon: <PhotoLibrary /> },
        { label: 'Companies', link: '/barcodes/companies', icon: <Business /> },
      ]
    },
    {
      key: 'master',
      label: 'Master Lists',
      icon: <Inventory />,
      expandable: true,
      children: [
        { 
          label: 'Final Audit & Images', 
          link: '/masters/final_Audit&Images', 
          icon: <PhotoLibrary /> 
        },
        { 
          label: 'Final Barcode', 
          link: '/masters/final_barcode', 
          icon: <QrCode /> 
        },
      ]
    },
    {
      key: 'countries',
      label: 'Countries',
      icon: <Public />,
      link: '/countries'
    },
    {
      key: 'contributors',
      label: 'Contributors / Access',
      icon: <Group />,
      link: '/contributors',
      show: user?.role === 'admin'
    },
    {
      key: 'manual_search',
      label: 'Manual Search',
      icon: <Search />,
      link: '/manual_search'
    },
    {
      key: 'search_logs',
      label: 'Search Logs',
      icon: <History />,
      link: '/search-logs'
    },
  ];

  const drawer = (
    <Box sx={{ 
      width: 250, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(0,0,0,0.3)',
        },
      }}>
        <List sx={{ p: 1, mt: 1 }}>
          {menuItems.map((item) => {
            if (item.show === false) return null;
            
            if (item.expandable) {
              return (
                <Box key={item.key}>
                  <ListItemButton 
                    onClick={() => toggleMenu(item.key)}
                    sx={{ 
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label} 
                      primaryTypographyProps={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem'
                      }}
                    />
                    {openMenus[item.key] ? 
                      <ExpandLess sx={{ color: 'primary.main' }} /> : 
                      <ExpandMore sx={{ color: 'text.secondary' }} />
                    }
                  </ListItemButton>
                  
                  <Collapse in={openMenus[item.key]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => (
                        child.action ? (
                          <ListItemButton 
                            key={child.label}
                            onClick={child.action}
                            sx={{ 
                              pl: 6, 
                              py: 1,
                              borderRadius: '0 20px 20px 0',
                              '&:hover': {
                                backgroundColor: 'rgba(245, 124, 0, 0.08)',
                                borderLeft: '3px solid #f57c00',
                              }
                            }}
                          >
                            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 32 }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={child.label}
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: 400
                              }}
                            />
                          </ListItemButton>
                        ) : (
                          <ListItemButton 
                            key={child.label}
                            component={Link} 
                            to={child.link} 
                            sx={{ 
                              pl: 6, 
                              py: 1,
                              borderRadius: '0 20px 20px 0',
                              '&:hover': {
                                backgroundColor: 'rgba(245, 124, 0, 0.08)',
                                borderLeft: '3px solid #f57c00',
                              }
                            }}
                          >
                            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 32 }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={child.label}
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: 400
                              }}
                            />
                          </ListItemButton>
                        )
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItemButton 
                key={item.key}
                component={Link} 
                to={item.link}
                sx={{ 
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}
                />
                {item.chip && (
                  <Chip 
                    label={item.chip} 
                    size="small" 
                    sx={{ 
                      bgcolor: 'secondary.main',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  // Rest of the component remains the same...
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar sx={{ minHeight: 64 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                onClick={() => window.location.reload()}
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #ffffff, #e3f2fd)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.3rem',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                    transform: 'scale(1.01)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                WikiWaste Database
              </Typography>
            </Box>

            <Button
              color="inherit"
              startIcon={<AccountCircle />}
              onClick={handleMenuOpen}
              sx={{ 
                textTransform: 'none',
                borderRadius: 20,
                px: 2,
                py: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {user?.username}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1 }}>
                  {user?.role}
                </Typography>
              </Box>
            </Button>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #e2e8f0',
                  mt: 1,
                  minWidth: 200,
                }
              }}
            >
              <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ 
                    width: 32, 
                    height: 32, 
                    mr: 2, 
                    bgcolor: 'primary.main',
                    fontSize: '0.9rem'
                  }}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {user?.username}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem 
                onClick={handleOpenPasswordDialog}
                sx={{ 
                  borderRadius: 1,
                  mx: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  }
                }}
              >
                <Settings fontSize="small" sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="body2">Change Password</Typography>
              </MenuItem>
              <MenuItem 
                onClick={logout}
                sx={{ 
                  borderRadius: 1,
                  mx: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  }
                }}
              >
                <ExitToApp fontSize="small" sx={{ mr: 2, color: 'error.main' }} />
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <ChangePasswordDialog 
          open={openPasswordDialog} 
          onClose={handleClosePasswordDialog} 
        />
        
        <Box component="nav" sx={{ width: { sm: 250 }, flexShrink: { sm: 0 } }}>
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{ 
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: 250,
                  height: '100%',
                  overflow: 'hidden'
                } 
              }}
            >
              {drawer}
            </Drawer>
          ) : (
            <Drawer
              variant="permanent"
              sx={{ 
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: 250,
                  height: '100%',
                  overflow: 'hidden'
                } 
              }}
              open
            >
              <Toolbar />
              {drawer}
            </Drawer>
          )}
        </Box>
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            width: { sm: `calc(100% - 250px)` },
            backgroundColor: 'background.default',
            height: '100vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Toolbar />
          <Box sx={{ 
            flex: 1, 
            p: 3,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(0,0,0,0.3)',
            },
          }}>
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Navbar;